import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  CheckCircle,
  Dumbbell,
  Users,
  TrendingUp,
  Apple,
  HelpCircle,
  User,
  Mail,
  Phone,
  FileText,
  Send,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const services = [
  { id: "personal-training", title: "Personal Training", icon: Dumbbell },
  { id: "groepslessen", title: "Groepslessen", icon: Users },
  { id: "transformatietrajecten", title: "Transformatietrajecten", icon: TrendingUp },
  { id: "voedingsbegeleiding", title: "Voedingsbegeleiding", icon: Apple },
  { id: "andere", title: "Andere", icon: HelpCircle },
];

const BookingPage = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isComplete, setIsComplete] = useState(false);
  const [selectedService, setSelectedService] = useState("");
  const [formData, setFormData] = useState({
    naam: "",
    email: "",
    telefoon: "",
    notities: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const canSubmit =
    selectedService && formData.naam && formData.email && formData.telefoon;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, {
        service_type: selectedService,
        naam: formData.naam,
        email: formData.email,
        telefoon: formData.telefoon,
        notities: formData.notities,
      });
      setIsComplete(true);
      toast.success("Aanvraag succesvol verzonden!");
    } catch {
      toast.error("Er ging iets mis. Probeer het opnieuw.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isComplete) {
    return (
      <main data-testid="booking-complete" className="pt-20 min-h-screen bg-[#0A0A0A]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-lg mx-auto text-center"
          >
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-red-500" />
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-white mb-4 font-['Barlow_Condensed'] uppercase">
              AANVRAAG <span className="text-red-500">VERZONDEN!</span>
            </h1>
            <p className="text-zinc-400 mb-8">
              We hebben je intake aanvraag ontvangen en nemen zo snel mogelijk
              contact met je op.
            </p>
            <div className="bg-zinc-900/50 border border-zinc-800 p-6 rounded-sm mb-8 text-left">
              <h3 className="text-white font-bold mb-4">Overzicht</h3>
              <ul className="space-y-2 text-sm">
                <li className="flex justify-between">
                  <span className="text-zinc-500">Dienst:</span>
                  <span className="text-white">
                    {services.find((s) => s.id === selectedService)?.title}
                  </span>
                </li>
                <li className="flex justify-between">
                  <span className="text-zinc-500">Naam:</span>
                  <span className="text-white">{formData.naam}</span>
                </li>
                <li className="flex justify-between">
                  <span className="text-zinc-500">E-mail:</span>
                  <span className="text-white">{formData.email}</span>
                </li>
                <li className="flex justify-between">
                  <span className="text-zinc-500">Telefoon:</span>
                  <span className="text-white">{formData.telefoon}</span>
                </li>
              </ul>
            </div>
            <button
              onClick={() => navigate("/")}
              className="btn-primary"
              data-testid="back-home-btn"
            >
              Terug naar Home
            </button>
          </motion.div>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="booking-page" className="pt-20 min-h-screen bg-[#0A0A0A]">
      <section className="py-12 bg-[#0A0A0A]">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <span className="caption mb-4 block">Intake Aanvraag</span>
            <h1 className="text-4xl md:text-5xl font-black text-white font-['Barlow_Condensed'] uppercase tracking-tighter">
              Boek Je <span className="text-red-500">Intake</span>
            </h1>
            <p className="text-zinc-400 mt-4 max-w-xl mx-auto">
              Vul het formulier in en wij nemen zo snel mogelijk contact met je
              op om je intake in te plannen.
            </p>
          </motion.div>
        </div>
      </section>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <motion.form
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          onSubmit={handleSubmit}
          className="max-w-2xl mx-auto space-y-8"
        >
          {/* Dienst selectie */}
          <div>
            <h2 className="text-xl font-black text-white mb-4 font-['Barlow_Condensed'] uppercase">
              Waarvoor wil je een intake?
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {services.map((service) => {
                const Icon = service.icon;
                const isSelected = selectedService === service.id;
                return (
                  <button
                    key={service.id}
                    type="button"
                    onClick={() => setSelectedService(service.id)}
                    className={`p-4 rounded-sm border text-center transition-all duration-300 ${
                      isSelected
                        ? "border-red-500 bg-red-500/10"
                        : "border-zinc-800 bg-zinc-900/50 hover:border-zinc-700"
                    }`}
                    data-testid={`service-option-${service.id}`}
                  >
                    <Icon
                      className={`w-6 h-6 mx-auto mb-2 ${
                        isSelected ? "text-red-500" : "text-zinc-500"
                      }`}
                    />
                    <span
                      className={`text-sm font-bold ${
                        isSelected ? "text-white" : "text-zinc-400"
                      }`}
                    >
                      {service.title}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Persoonlijke gegevens */}
          <div className="space-y-5">
            <h2 className="text-xl font-black text-white font-['Barlow_Condensed'] uppercase">
              Jouw Gegevens
            </h2>

            <div>
              <label className="flex items-center gap-2 text-sm text-zinc-400 mb-2">
                <User className="w-4 h-4" /> Naam *
              </label>
              <input
                type="text"
                name="naam"
                value={formData.naam}
                onChange={handleChange}
                required
                className="form-input"
                placeholder="Je volledige naam"
                data-testid="booking-naam-input"
              />
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm text-zinc-400 mb-2">
                <Mail className="w-4 h-4" /> E-mail *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                className="form-input"
                placeholder="je@email.com"
                data-testid="booking-email-input"
              />
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm text-zinc-400 mb-2">
                <Phone className="w-4 h-4" /> Telefoon *
              </label>
              <input
                type="tel"
                name="telefoon"
                value={formData.telefoon}
                onChange={handleChange}
                required
                className="form-input"
                placeholder="0479 12 34 56"
                data-testid="booking-telefoon-input"
              />
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm text-zinc-400 mb-2">
                <FileText className="w-4 h-4" /> Extra notitie (optioneel)
              </label>
              <textarea
                name="notities"
                value={formData.notities}
                onChange={handleChange}
                rows={4}
                className="form-input resize-none"
                placeholder="Eventuele bijzonderheden, doelen of vragen"
                data-testid="booking-notities-textarea"
              />
            </div>
          </div>

          {/* Submit */}
          <div className="pt-4">
            <button
              type="submit"
              disabled={!canSubmit || isSubmitting}
              className={`btn-primary w-full flex items-center justify-center gap-2 ${
                !canSubmit || isSubmitting ? "opacity-50 cursor-not-allowed" : ""
              }`}
              data-testid="confirm-booking-btn"
            >
              {isSubmitting ? (
                "Bezig met verzenden..."
              ) : (
                <>
                  Verstuur Aanvraag
                  <Send size={18} />
                </>
              )}
            </button>
            <p className="text-zinc-600 text-xs text-center mt-4">
              Door te verzenden ga je akkoord met onze voorwaarden.
            </p>
          </div>
        </motion.form>
      </div>
    </main>
  );
};

export default BookingPage;
